import { useEffect, useRef, type RefObject } from 'react';
import { useDocumentDrawer, useOpenDocument, usePendingHandoff } from '../DocumentDrawerProvider.tsx';

const TRAVEL_DISTANCE = '2rem';
const DURATION_MS = 280;
const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

const HANDOFF_TRAVEL_DISTANCE = '3rem';
const HANDOFF_EXIT_MS = 260;
const HANDOFF_ENTER_MS = 420;
const HANDOFF_EXIT_EASING = 'cubic-bezier(0.55, 0, 1, 0.45)';

function prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useDocumentTransitions(contentRef: RefObject<HTMLElement | null>): void {
    const session = useOpenDocument();
    const handoff = usePendingHandoff();
    const { finishHandoff } = useDocumentDrawer();
    const documentId = session?.documentId ?? '';
    const currentIndex = session?.documentList.findIndex((doc) => doc.id === documentId) ?? -1;

    const previousDocumentId = useRef('');
    const previousIndex = useRef(-1);
    const exit = useRef<Animation | null>(null);

    useEffect(() => {
        const cameFrom = previousDocumentId.current;
        const movedBackwards =
            currentIndex >= 0 &&
            previousIndex.current >= 0 &&
            currentIndex < previousIndex.current;
        previousDocumentId.current = documentId;
        previousIndex.current = currentIndex;

        const followsAHandoff = exit.current !== null;
        exit.current?.cancel();
        exit.current = null;

        const isFirstDocumentOfTheSession = !cameFrom || cameFrom === documentId;
        if (!documentId || isFirstDocumentOfTheSession) {
            return;
        }
        const element = contentRef.current;
        if (!element || prefersReducedMotion()) {
            return;
        }
        const entersFrom = followsAHandoff
            ? HANDOFF_TRAVEL_DISTANCE
            : movedBackwards
              ? `-${TRAVEL_DISTANCE}`
              : TRAVEL_DISTANCE;
        element.animate(
            [
                { opacity: 0, transform: `translateX(${entersFrom})` },
                { opacity: 1, transform: 'none' },
            ],
            { duration: followsAHandoff ? HANDOFF_ENTER_MS : DURATION_MS, easing: EASING },
        );
    }, [contentRef, documentId, currentIndex]);

    useEffect(() => {
        if (!handoff) {
            return;
        }
        const element = contentRef.current;
        if (!element || prefersReducedMotion()) {
            finishHandoff();
            return;
        }
        let abandoned = false;
        exit.current = element.animate(
            [
                { opacity: 1, transform: 'none' },
                { opacity: 0, transform: `translateX(-${HANDOFF_TRAVEL_DISTANCE})` },
            ],
            { duration: HANDOFF_EXIT_MS, easing: HANDOFF_EXIT_EASING, fill: 'forwards' },
        );
        void exit.current.finished
            .catch(() => {})
            .then(() => {
                if (!abandoned) {
                    finishHandoff();
                }
            });
        return () => {
            abandoned = true;
        };
    }, [handoff, finishHandoff, contentRef]);
}
